import { accessToken, sessionRejected } from './auth.js';
import { clientName } from './client.js';

/**
 * Thin API client.
 *
 * Server errors carry a readable `error` string; this surfaces it as the
 * Error message so screens can render it directly instead of showing
 * "Request failed" and hiding the reason.
 *
 * Every call carries the Supabase access token as a bearer header. There is no
 * ambient credential and no cookie, so a cross-site request cannot borrow the
 * session — this design has no CSRF surface to defend.
 *
 * A 401 does not navigate anywhere. It ends the local session and lets the app
 * render its sign-in screen, which is why there are no redirect-loop guards
 * here any more: nothing redirects, so nothing can loop.
 */

/**
 * The server saying, in so many words, that this account has no pantry.
 *
 * App.jsx decides whether to show the first-run screen from
 * `/household/pending`, and that is the right primary source: a client that
 * infers its own state from an error mistakes an outage for an empty account.
 * But it left that one request as a single point of failure. When it failed,
 * `has_household` was undefined, `undefined === false` was false, and the app
 * concluded the account HAD a pantry -- then rendered a shell in which every
 * screen 403s and no button leads to creating or joining one. Stranded, with
 * no way out but signing out.
 *
 * So this is a backstop, and it is deliberately narrow. Only the explicit
 * `NO_HOUSEHOLD` code sets it -- never a timeout, a 500, or a parse failure.
 * That distinction is the whole point: a refusal naming its own reason is the
 * server stating a fact about the account, while a failed request is the
 * server failing to state anything, and the original comment is right that
 * those must not be confused.
 *
 * Cleared when a session ends, so signing in as somebody else in the same tab
 * does not inherit the last account's answer.
 */
let noHousehold = false;
const listeners = new Set();

function setNoHousehold(value) {
  if (noHousehold === value) return;
  noHousehold = value;
  for (const listener of listeners) listener();
}

/** For useSyncExternalStore. */
export function subscribeNoHousehold(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const sawNoHousehold = () => noHousehold;

async function request(method, path, body, options = {}) {
  const isForm = body instanceof FormData;
  const headers = {};
  // FormData sets its own content-type, boundary included; naming it here
  // would produce a body the server cannot parse.
  if (body && !isForm) headers['content-type'] = 'application/json';
  const token = await accessToken();
  if (token) headers.authorization = `Bearer ${token}`;

  // Where this install came from, so the funnel in the admin console can say
  // whether Play brings households that stay. A hint and nothing more: the
  // server validates it against a list it owns and stamps it once, and nothing
  // that matters -- no permission, no plan, no quota -- reads it.
  const client = clientName();
  if (client) headers['x-pantry-client'] = client;

  const response = await fetch(`/api${path}`, {
    method,
    headers,
    body: isForm ? body : body ? JSON.stringify(body) : undefined,
    signal: options.signal,
  });

  const text = await response.text();
  let data = null;
  if (text) {
    try { data = JSON.parse(text); } catch { data = text; }
  }

  if (!response.ok) {
    // 401 is the only signal that the session itself is finished — an expired
    // token, or none sent at all. 403 means signed in but not permitted, and
    // the body explains which rule refused: that is a message to read, not a
    // session to discard.
    if (response.status === 401 || data?.code === 'AUTH_REQUIRED') {
      setNoHousehold(false);
      await sessionRejected(data?.error);
    }
    if (data?.code === 'NO_HOUSEHOLD') setNoHousehold(true);
    const error = new Error(data?.error || `Request failed (${response.status})`);
    if (data?.hint) error.hint = data.hint;
    error.status = response.status;
    error.code = data?.code;
    error.data = data;
    throw error;
  }
  return data;
}

/**
 * Fetch a file rather than a parsed body.
 *
 * The data export is an authenticated download, so a plain <a href> will not
 * do -- it carries no bearer token and would come back a 401. This carries the
 * token the same way every other call does and hands back the bytes.
 */
async function requestBlob(path) {
  const headers = {};
  const token = await accessToken();
  if (token) headers.authorization = `Bearer ${token}`;

  const response = await fetch(`/api${path}`, { headers });
  if (!response.ok) {
    const text = await response.text();
    let data = null;
    try { data = JSON.parse(text); } catch { /* not json */ }
    if (response.status === 401 || data?.code === 'AUTH_REQUIRED') {
      setNoHousehold(false);
      await sessionRejected(data?.error);
    }
    if (data?.code === 'NO_HOUSEHOLD') setNoHousehold(true);
    const error = new Error(data?.error || `Download failed (${response.status})`);
    error.status = response.status;
    error.code = data?.code;
    throw error;
  }
  return response.blob();
}

export const api = {
  get: (path, options) => request('GET', path, null, options),
  post: (path, body, options) => request('POST', path, body, options),
  patch: (path, body, options) => request('PATCH', path, body, options),
  put: (path, body, options) => request('PUT', path, body, options),
  delete: (path, options) => request('DELETE', path, null, options),
  upload: (path, formData, options) => request('POST', path, formData, options),
  blob: (path) => requestBlob(path),
};

export const qs = (params) => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const string = search.toString();
  return string ? `?${string}` : '';
};
